import _ from "lodash";

/**
 * axios 的 error → 給人看的訊息。全站共用，任何 catch 都該走這裡。
 *
 * 為什麼需要這支：
 * 後端被 [PermissionOnly("xxx.delete")] 擋下來時，走的是 ASP.NET 授權管線，
 * 不會進到 controller，回的是「403 + 空 body」——沒有 res_msg 可讀。
 * 各頁原本寫的是 `res_msg || "刪除失敗"`，於是使用者看到的是「刪除失敗」
 * 或「系統發生錯誤！」，看起來就像系統壞了，實際上只是他的角色沒開這個權限。
 * 權限不足必須明講，而且要講「該找誰處理」，不然只會變成一張除錯工單。
 *
 * fallback 只在「有回應、但既沒有 res_msg 也不是已知狀態碼」時才會用到。
 */
export function apiErrorMessage(error, fallback = "系統發生錯誤！") {
  // 後端自己回的訊息最精準（例如「刪除失敗: 里程碑底下還有 3 個工項」），優先採用
  const msg = _.get(error, "response.data.res_msg");
  if (msg) return String(msg);

  const status = _.get(error, "response.status");
  if (status === 403) return "權限不足：您的帳號沒有執行這項操作的權限，請聯絡系統管理員開通權限。";
  if (status === 401) return "登入狀態已失效，請重新登入。";
  if (status === 404) return "找不到資料，資料可能已被刪除，請重新整理後再試。";
  if (status === 413) return "檔案大小超過上限，請改用較小的檔案。";
  if (status >= 500) return "伺服器發生錯誤，請稍後再試；若問題持續，請聯絡系統管理員。";

  /*
   * 到這裡代表沒有 HTTP 回應。有兩種完全不同的情況，不能混為一談：
   *
   *   a) 真的沒送到 —— 斷線、逾時、CORS。這才是「無法連線到伺服器」。
   *   b) 我們自己 throw 的 Error，例如工作流守門的
   *      new Error("尚有驗收條件未完成")。它一樣沒有 response。
   *
   * 原本沒有分，於是 (b) 全部被講成「無法連線到伺服器，請確認網路連線」——
   * 使用者去查網路，真正的原因（DoD 沒勾完、狀態不允許轉換）反而不見了。
   * 以 axios 自己的標記分辨：只有 axios 丟出來的才可能是連線問題。
   */
  const fromHttp = !!_.get(error, "isAxiosError") || !!_.get(error, "config") || !!_.get(error, "request");
  if (fromHttp) {
    if (_.get(error, "code") === "ECONNABORTED") return "連線逾時，請檢查網路連線後再試。";
    if (!error.response) return "無法連線到伺服器，請確認網路連線是否正常。";
  }

  // 自己 throw 的 Error：訊息本來就是寫給人看的，直接用
  const own = _.get(error, "message");
  if (own) return String(own);

  return fallback;
}

export default apiErrorMessage;
